import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { DatePicker } from "@mui/x-date-pickers";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { toast } from "react-toastify";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px", 
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const headLabel = [
  {
    id: 1,
    label: "SNO",
    value: "sno",
  },
  {
    id: 2,
    label: "Collection Date",
    value: "collectionDate",
  },
  {
    id: 3,
    label: "Receipt No",
    value: "receiptNo",
  },
  {
    id: 4,
    label: "Patient UHID", 
    value: "patientUhid",
  },
  {
    id: 5,
    label: "Patient Name",
    value: "patientName",
  },
  {
    id: 6,
    label: "Collection Type",
    value: "collectionType",
  },
  {
    id: 7,
    label: "Amount",
    value: "amount",
  },
  {
    id: 8,
    label: "Payment Mode",
    value: "paymentMode",
  },
  {
    id: 9,
    label: "Collected By",
    value: "collectedBy",
  },
];

// Proper dropdown options for Collection Report
const templateOptions = [
  { label: "All Collections", value: "all_collections" },
  { label: "Cash Collections", value: "cash_collections" },
  { label: "Card Collections", value: "card_collections" },
  { label: "UPI Collections", value: "upi_collections" },
  { label: "OPD Collections", value: "opd_collections" },
  { label: "IPD Collections", value: "ipd_collections" },
  { label: "Pharmacy Collections", value: "pharmacy_collections" },
  { label: "Lab Collections", value: "lab_collections" },
];

export default function ReportsCollection() {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    searchTemplate: null,
    date: null,
  });

  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [errors, setErrors] = useState([]); // For required field validation errors

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    sno: true,
    collectionDate: true,
    receiptNo: true,
    patientUhid: true,
    patientName: true,
    collectionType: true,
    amount: true,
    paymentMode: true,
    collectedBy: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Collection Report",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.info("Link copied to clipboard");
    }
  };

  const handleMenuClick = (item) => {
    handleClose();
    if (item === "Excel") {
      handleExport();
    } else if (item === "PDF") {
      toast.info("PDF export feature coming soon");
    } else {
      toast.info(`${item} feature coming soon`);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    // Validate required fields and collect error field names
    const errorFields = [];
    if (!formData.date) {
      errorFields.push('date');
    }
    
    if (errorFields.length > 0) {
      setErrors(errorFields);
      toast.error("Please fill all the required fields.");
      return;
    }
    
    // Clear errors if validation passes
    setErrors([]);
    
    setIsLoading(true);
    
    try {
      // Simulate API call - replace with actual API endpoint
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock data for demonstration - replace with actual API response
      const mockData = [
        {
          _id: "1",
          sno: "1",
          collectionDate: "2024-01-15",
          receiptNo: "RCP001",
          patientUhid: "KSG-24-0000022",
          patientName: "John Doe",
          collectionType: "OPD Consultation",
          amount: "₹500.00",
          paymentMode: "Cash",
          collectedBy: "Dr. Smith"
        },
        {
          _id: "2", 
          sno: "2",
          collectionDate: "2024-01-15",
          receiptNo: "RCP002",
          patientUhid: "KSG-24-0000023",
          patientName: "Jane Smith",
          collectionType: "Lab Test",
          amount: "₹1,200.00",
          paymentMode: "Card",
          collectedBy: "Lab Tech"
        },
        {
          _id: "3",
          sno: "3", 
          collectionDate: "2024-01-14",
          receiptNo: "RCP003",
          patientUhid: "KSG-24-0000024",
          patientName: "Michael Johnson",
          collectionType: "Pharmacy",
          amount: "₹350.00",
          paymentMode: "UPI",
          collectedBy: "Pharmacist"
        },
        {
          _id: "4",
          sno: "4",
          collectionDate: "2024-01-14", 
          receiptNo: "RCP004",
          patientUhid: "KSG-24-0000025",
          patientName: "Emily Davis",
          collectionType: "IPD Admission",
          amount: "₹5,000.00",
          paymentMode: "Cash",
          collectedBy: "Admission Desk"
        },
        {
          _id: "5",
          sno: "5",
          collectionDate: "2024-01-13",
          receiptNo: "RCP005", 
          patientUhid: "KSG-24-0000026",
          patientName: "Robert Brown",
          collectionType: "Emergency Fee",
          amount: "₹800.00",
          paymentMode: "Card",
          collectedBy: "Emergency Staff"
        }
      ];
      
      setTableData(mockData);
      toast.success("Collection data loaded successfully");
    } catch (error) {
      console.error("Error fetching collection data:", error);
      toast.error("Failed to load collection data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExport = async () => {
    if (tableData.length === 0) {
      toast.warning("Please search for data before exporting");
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Simulate export process - replace with actual export logic
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success("Collection report exported successfully");
    } catch (error) {
      console.error("Error exporting report:", error);
      toast.error("Failed to export report");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>Collection Report</Typography>
          </Box>
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={templateOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DatePicker
                  name="date"
                  label="Date"
                  value={formData.date}
                  onChange={(newValue) => {
                    handleInputChange('date', newValue);
                    // Clear error when field is changed
                    if (errors.includes('date')) {
                      setErrors(errors.filter(e => e !== 'date'));
                    }
                  }}
                  slotProps={{
                    textField: { 
                      required: true, 
                      fullWidth: true,
                      InputLabelProps: { required: true, sx: { '& .MuiFormLabel-asterisk': { color: 'red' } } }
                    },
                  }}
                  sx={{ 
                    bgcolor: "white",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        border: errors?.includes('date') ? '1px solid red' : '1px solid inherit',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: "20px",
              gap: 2,
            }}
          >
            <Button
              variant="contained"
              color="secondary"
              onClick={handleExport}
              disabled={isExporting || tableData.length === 0}
              sx={{ minWidth: 120 }}
            >
              {isExporting ? <CircularProgress size={20} /> : 'Export'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={isLoading}
              sx={{
                textTransform: "none",
                backgroundColor: "rgba(1, 121, 176, 1)",
                width: "150px",
              }}
            >
              {isLoading ? <CircularProgress size={20} /> : 'Search'}
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
              {/* dynamic button */}
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: 1,
                    pb: "15px",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                  >
                    Rows: {tableData?.length || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Box sx={iconStyleProps} onClick={handleFilterClick}>
                      <EditIcon
                        sx={{
                          cursor: "pointer",
                          color: "transparent",
                          fontSize: "18px",
                        }}
                      />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleShare}>
                      <ShareIcon />
                    </Box>
                    <Box sx={iconStyleProps} onClick={handleClick}>
                      <IconButton>
                        <FileDownloadOutlinedIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <TableContainer component={Paper}>
                <Table aria-label="collection report table" sx={{ minWidth: 800 }}>
                  <TableHead
                    sx={{
                      backgroundColor: "#f5f5f5",
                      "& th": { fontWeight: "bold" },
                    }}
                  >
                    <TableRow>
                      {visibleColumns.sno && <TableCell>SNO</TableCell>}
                      {visibleColumns.collectionDate && <TableCell>Collection Date</TableCell>}
                      {visibleColumns.receiptNo && <TableCell>Receipt No</TableCell>}
                      {visibleColumns.patientUhid && <TableCell>Patient UHID</TableCell>}
                      {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                      {visibleColumns.collectionType && <TableCell>Collection Type</TableCell>}
                      {visibleColumns.amount && <TableCell>Amount</TableCell>}
                      {visibleColumns.paymentMode && <TableCell>Payment Mode</TableCell>}
                      {visibleColumns.collectedBy && <TableCell>Collected By</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isLoading ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : !Array.isArray(tableData) || tableData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((row) => (
                        <TableRow key={row?._id}>
                          {visibleColumns.sno && <TableCell>{row?.sno}</TableCell>}
                          {visibleColumns.collectionDate && <TableCell>{row?.collectionDate}</TableCell>}
                          {visibleColumns.receiptNo && <TableCell>{row?.receiptNo}</TableCell>}
                          {visibleColumns.patientUhid && <TableCell>{row?.patientUhid}</TableCell>}
                          {visibleColumns.patientName && <TableCell>{row?.patientName}</TableCell>}
                          {visibleColumns.collectionType && <TableCell>{row?.collectionType}</TableCell>}
                          {visibleColumns.amount && <TableCell>{row?.amount}</TableCell>}
                          {visibleColumns.paymentMode && <TableCell>{row?.paymentMode}</TableCell>}
                          {visibleColumns.collectedBy && <TableCell>{row?.collectedBy}</TableCell>}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
          </Box>

              {/* Download Menu */}
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "250px",
                  },
                }}
              >
                {menuItems.map((item, index) => (
                  <React.Fragment key={item}>
                    <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                      {item}
                    </MenuItem>
                    {index !== menuItems.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Menu>

              {/* Filter Menu for Column Visibility */}
              <Menu
                anchorEl={filterAnchorEl}
                open={filterMenuOpen}
                onClose={handleFilterClose}
                slotProps={{
                  sx: {
                    borderRadius: "12px",
                    boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                    padding: "8px 0",
                    width: "200px",
                  },
                }}
              >
                <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
                  Show/Hide Columns
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => toggleColumn('sno')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>SNO</Box>
                    {visibleColumns.sno && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('collectionDate')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Collection Date</Box>
                    {visibleColumns.collectionDate && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('receiptNo')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Receipt No</Box>
                    {visibleColumns.receiptNo && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientUhid')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient UHID</Box>
                    {visibleColumns.patientUhid && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Patient Name</Box>
                    {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('collectionType')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Collection Type</Box>
                    {visibleColumns.collectionType && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('amount')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Amount</Box>
                    {visibleColumns.amount && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('paymentMode')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Payment Mode</Box>
                    {visibleColumns.paymentMode && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
                <MenuItem onClick={() => toggleColumn('collectedBy')} sx={menuItemStyle}>
                  <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Box sx={{ flex: 1 }}>Collected By</Box>
                    {visibleColumns.collectedBy && <Box sx={{ color: 'primary.main' }}>✓</Box>}
                  </Box>
                </MenuItem>
              </Menu>
        </Box>
      </Box>
    </Container>
  );
}
